/**
 * プリセットアイコン選択ステップ
 */
import { useSelector } from "react-redux";
import { useMemo, useState, useEffect } from "react";
import { RootState } from "app/store";
import { userIconPrefixes } from "app/assets";
import Constants from "common/constant";
import I18n from "features/shared/i18n";
import { CheckMark } from "features/user/assets";
import { CreatePlayerState, PlayerGender } from "../types";

type IconCategory = "NONE" | "BOY" | "GIRL";

const TAB_OPTIONS = [
  { key: "NONE" as IconCategory, labelKey: "MSG_ICON_CATEGORY_ALL_BY_PARENT" },
  { key: "BOY" as IconCategory, labelKey: "MSG_ICON_CATEGORY_BOY_BY_PARENT" },
  { key: "GIRL" as IconCategory, labelKey: "MSG_ICON_CATEGORY_GIRL_BY_PARENT" },
];

interface IconData {
  url: string;
  category: IconCategory;
}

interface PlayerPresetIconStepProps {
  state: CreatePlayerState;
  updateState: (updates: Partial<CreatePlayerState>) => void;
}

const IconItem = ({
  icon,
  selected,
  onSelect,
}: {
  icon: IconData;
  selected: boolean;
  onSelect: () => void;
}) => {
  return (
    <div onClick={onSelect} className="flex-col-center w-[90px] h-[90px] cursor-pointer">
      {selected && (
        <div className="flex-col-el absolute top-[-16px] left-[-16px]">
          <CheckMark />
        </div>
      )}
      <img
        alt="user-icon"
        src={icon.url}
        className={`w-full h-full box-border object-contain border-solid border-6 ${
          selected ? "border-green-100/40" : "border-gray-80/40"
        }`}
      />
    </div>
  );
};

export const PlayerPresetIconStep = ({ state, updateState }: PlayerPresetIconStepProps) => {
  const [selectedTab, setSelectedTab] = useState<IconCategory>("NONE");
  const defaultAssets = useSelector((s: RootState) => s.config.assets);

  // アイコン一覧を取得
  const icons = useMemo(() => {
    try {
      type AssetItem = { fileSize: number; fileKey: string; category: string };
      return userIconPrefixes
        .reduce((pre: AssetItem[], cur: string) => [...pre, ...defaultAssets[cur]], [])
        .filter((a: AssetItem) => a.fileSize > 0)
        .map((a: AssetItem) => ({
          url: `${Constants.assetHost}/${a.fileKey}`,
          category: a.category as IconCategory,
        }));
    } catch {
      return [] as IconData[];
    }
  }, [defaultAssets]);

  // 初期アイコンを設定
  useEffect(() => {
    if (!state.icon && icons.length > 0) {
      const first = icons.find((i) => i.category === selectedTab) || icons[0];
      if (first) {
        updateState({ icon: first.url });
      }
    }
  }, [icons, selectedTab, state.icon, updateState]);

  const handleTabChange = (tab: IconCategory) => {
    setSelectedTab(tab);
  };

  const handleSelectIcon = (icon: IconData) => {
    const gender =
      icon.category === "BOY"
        ? PlayerGender.MALE
        : icon.category === "GIRL"
          ? PlayerGender.FEMALE
          : PlayerGender.NONE;
    updateState({ icon: icon.url, gender });
  };

  const filteredIcons =
    selectedTab === "NONE" ? icons : icons.filter((i) => i.category === selectedTab);

  return (
    <div className="flex-col-el flex-center w-full h-full">
      {/* タイトル */}
      <div className="flex-col-el flex-center mb-4">
        <p className="text-body text-textcolor-black text-[32px]">
          {I18n.t("MSG_PRESET_ICON_TITLE_BY_PARENT")}
        </p>
      </div>

      {/* タブ切り替え */}
      <div className="flex-row-center w-[516px] h-[74px] mb-4 bg-beige-60 rounded-[12px] border-[4px] border-solid border-white">
        {TAB_OPTIONS.map((option, index) => {
          const isActive = selectedTab === option.key;
          const isFirst = index === 0;
          const isLast = index === TAB_OPTIONS.length - 1;
          return (
            <div
              key={option.key}
              onClick={() => handleTabChange(option.key)}
              className={`flex-col-center cursor-pointer flex-1 h-full ${
                isActive ? "bg-green-alt" : ""
              } ${isFirst ? "rounded-l-[8px]" : ""} ${isLast ? "rounded-r-[8px]" : ""}`}
            >
              <p className="text text-textcolor-black !text-[20px] !leading-[22px]">
                {I18n.t(option.labelKey)}
              </p>
            </div>
          );
        })}
      </div>

      {/* アイコングリッド */}
      <div className="flex-col-el overflow-y-auto p-4">
        <div className="grid grid-cols-5 grid-rows-3 gap-6">
          {filteredIcons.map((icon) => (
            <IconItem
              key={icon.url}
              icon={icon}
              selected={state.icon === icon.url}
              onSelect={() => handleSelectIcon(icon)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
